import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '../services/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import { DataTable } from '@/components/DataTable';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { Wallet, Trash2, Pencil } from 'lucide-react';

interface Budget { id: string; scope: string; scopeId: string; level: string; period: string | null; hours: number; amountChf: number | null; }
interface Project { id: string; name: string; code: string; }

export default function BudgetsPage() {
  const { canWrite } = usePermissions({ pageKey: 'budgets' });
  const { t } = useTranslation();

  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ scope: 'PROJECT', level: 'TOTAL', period: '', hours: '' });
  const [busy, setBusy] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editBudget, setEditBudget] = useState<Budget | null>(null);
  const [editLevel, setEditLevel] = useState('TOTAL');
  const [editPeriod, setEditPeriod] = useState('');
  const [editHours, setEditHours] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  useEffect(() => { async function f() { try { const d: Project[] = await api('/projects'); setProjects(d); if (d.length > 0) setSelectedProjectId(d[0].id); } catch (err: any) { setError(err.message); } finally { setLoading(false); } } f(); }, []);
  useEffect(() => { if (!selectedProjectId) return; fetchBudgets(); }, [selectedProjectId]);

  async function fetchBudgets() {
    try { const d: Budget[] = await api(`/budgets?scope=PROJECT&scopeId=${selectedProjectId}`); setBudgets(d); }
    catch (err: any) { setError(err.message); }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault(); setBusy(true);
    try {
      await api('/budgets', { method: 'POST', body: JSON.stringify({ scope: 'PROJECT', scopeId: selectedProjectId, level: form.level, period: form.period || undefined, hours: parseFloat(form.hours) }) });
      setForm({ ...form, period: '', hours: '' }); fetchBudgets();
    } catch (err: any) { setError(err.message); } finally { setBusy(false); }
  }

  async function handleDelete(id: string) { try { await api(`/budgets/${id}`, { method: 'DELETE' }); fetchBudgets(); } catch {} }

  function handleOpenEdit(b: Budget) {
    setEditBudget(b);
    setEditLevel(b.level);
    setEditPeriod(b.period || '');
    setEditHours(String(b.hours));
    setEditAmount(b.amountChf ? String(b.amountChf) : '');
    setEditOpen(true);
  }

  async function handleEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editBudget) return;
    setEditBusy(true);
    try {
      await api(`/budgets/${editBudget.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          level: editLevel,
          period: editPeriod || undefined,
          hours: parseFloat(editHours),
          amountChf: editAmount ? parseFloat(editAmount) : undefined,
        }),
      });
      setEditOpen(false);
      setEditBudget(null);
      fetchBudgets();
    } catch (err: any) { setError(err.message); }
    setEditBusy(false);
  }

  const maxHours = Math.max(1, ...budgets.map(b => b.hours));

  const mobileRender = (b: Budget) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{b.level}</span>
        <span className="font-mono text-xs font-medium">{Number(b.hours).toFixed(1)}h</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-[var(--color-primary)] transition-all" style={{ width: `${Math.min(100, (b.hours / maxHours) * 100)}%` }} />
      </div>
      <div className="flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
        <span>{b.period || 'No period'}</span>
        {b.amountChf && <span>{Number(b.amountChf).toFixed(2)} CHF</span>}
      </div>
      {canWrite && (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" className="h-7 text-[var(--color-muted-foreground)]" onClick={() => handleOpenEdit(b)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
          <Button variant="ghost" size="sm" className="h-7 text-rose-600" onClick={() => handleDelete(b.id)}><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
        </div>
      )}
    </div>
  );

  const columns = [
    { key: 'level', header: 'Level' },
    { key: 'period', header: 'Period', render: (b: Budget) => b.period || '-' },
    { key: 'hours', header: 'Hours', render: (b: Budget) => (
      <div className="w-40">
        <div className="mb-1 text-right text-xs font-mono">{Number(b.hours).toFixed(1)}h</div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-[var(--color-primary)] transition-all" style={{ width: `${Math.min(100, (b.hours / maxHours) * 100)}%` }} />
        </div>
      </div>
    )},
    { key: 'amount', header: 'Amount CHF', render: (b: Budget) => <span className="font-mono text-xs">{b.amountChf ? Number(b.amountChf).toFixed(2) : '-'}</span> },
    { key: 'actions', header: '', className: 'w-20', render: (b: Budget) => (
      canWrite ? (
        <div className="flex items-center gap-1">
          <Button data-testid="edit-budget-button" variant="ghost" size="icon" className="h-8 w-8 text-[var(--color-muted-foreground)]" onClick={() => handleOpenEdit(b)} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
          <Button data-testid="delete-budget-button" variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => handleDelete(b.id)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
        </div>
      ) : null
    )},
  ];

  return (
    <div className="animate-in space-y-6">
      <PageHeader title={t('budgets') || 'Budgets'} />

      <div className="flex items-center gap-3">
        <div className="w-full max-w-xs space-y-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">{t('project')}</label>
          <Select value={selectedProjectId} onValueChange={setSelectedProjectId} disabled={loading}>
            <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {canWrite && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Add Budget</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-3">
              <div className="w-40 space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Level</Label>
                <Select value={form.level} onValueChange={(v: string) => setForm({ ...form, level: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="TOTAL">Total</SelectItem><SelectItem value="YEAR">Year</SelectItem><SelectItem value="MONTH">Month</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="w-40 space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Period</Label>
                <Input type="text" placeholder="2026 or 2026-01" value={form.period} onChange={e => setForm({ ...form, period: e.target.value })} />
              </div>
              <div className="w-32 space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Hours</Label>
                <Input type="number" step="0.1" required value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} />
              </div>
              <Button data-testid="create-budget-button" type="submit" loading={busy} disabled={busy}>{t('create')}</Button>
            </form>
          </CardContent>
        </Card>
      )}

      {error && <Badge variant="destructive">{error}</Badge>}
      {loading ? <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-12 w-full" /></div> : (
        <DataTable
          columns={columns}
          data={budgets}
          keyExtractor={(b) => b.id}
          mobileRender={mobileRender}
          emptyState={<EmptyState icon={Wallet} title="No budgets" description="Add a budget for this project." />}
        />
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Budget</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Level</Label>
                <Select value={editLevel} onValueChange={(v: string) => setEditLevel(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="TOTAL">Total</SelectItem><SelectItem value="YEAR">Year</SelectItem><SelectItem value="MONTH">Month</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Period</Label>
                <Input type="text" placeholder="2026 or 2026-01" value={editPeriod} onChange={e => setEditPeriod(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Hours</Label>
                <Input type="number" step="0.1" required value={editHours} onChange={e => setEditHours(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Amount CHF</Label>
                <Input type="number" step="0.01" value={editAmount} onChange={e => setEditAmount(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" loading={editBusy} disabled={editBusy}>Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
